import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { 
  Database, ShieldCheck, Cpu, Scale, ChevronRight, 
  Activity, ArrowRightCircle, CheckCircle2, Save, Lock 
} from 'lucide-react';

// --- ANIMATION VARIANTS ---
const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 300, damping: 24 } }
};

// --- THE ASSESSMENT MATRIX ---
const QUESTION_BANK = [
  {
    id: 'd1', pillar: 'Data', icon: <Database className="w-5 h-5 text-blue-500" />,
    question: "How is your core enterprise data currently organized and stored?",
    options: [
      { text: "Centralized in cloud data warehouses/lakes with clean metadata.", points: 25 },
      { text: "Mostly digitized, but siloed across different SaaS apps and servers.", points: 10 },
      { text: "Highly fragmented, legacy on-premise systems, or paper-based.", points: 0 }
    ]
  },
  {
    id: 'd2', pillar: 'Data', icon: <Database className="w-5 h-5 text-blue-500" />,
    question: "Do you have established Access Control Lists (ACLs) mapped to user roles?",
    options: [
      { text: "Yes, strict Role-Based Access Control (RBAC) is enforced globally.", points: 25 },
      { text: "Partial RBAC. Some systems are secure, others have broad access.", points: 10 },
      { text: "No. Data access is loosely managed or granted ad-hoc.", points: 0 }
    ]
  },
  {
    id: 's1', pillar: 'Security', icon: <ShieldCheck className="w-5 h-5 text-emerald-500" />,
    question: "How do you currently handle sensitive PII/PHI in outbound API requests?",
    options: [
      { text: "Automated redaction/masking gateways are fully deployed.", points: 25 },
      { text: "We rely on employee training and manual policy enforcement.", points: 5 },
      { text: "We have no visibility into what data leaves the network via APIs.", points: 0 }
    ]
  },
  {
    id: 's2', pillar: 'Security', icon: <ShieldCheck className="w-5 h-5 text-emerald-500" />,
    question: "What is your defense against LLM Prompt Injection or Jailbreak attacks?",
    options: [
      { text: "We use an AI Firewall (like FCAIO) to intercept toxic payloads.", points: 25 },
      { text: "We rely on the LLM provider's built-in safety filters.", points: 10 },
      { text: "We have not implemented AI-specific attack mitigations.", points: 0 }
    ]
  },
  {
    id: 'i1', pillar: 'Infrastructure', icon: <Cpu className="w-5 h-5 text-purple-500" />,
    question: "What is your current cloud computing posture?",
    options: [
      { text: "Fully cloud-native (AWS/GCP/Azure) with scalable microservices.", points: 25 },
      { text: "Hybrid approach. Moving to the cloud, but relying on legacy servers.", points: 10 },
      { text: "Strictly on-premise air-gapped systems.", points: 5 }
    ]
  },
  {
    id: 'i2', pillar: 'Infrastructure', icon: <Cpu className="w-5 h-5 text-purple-500" />,
    question: "Do you have internal engineering talent familiar with Generative AI APIs?",
    options: [
      { text: "Yes, we have developers actively building with LLMs/RAG.", points: 25 },
      { text: "We have traditional developers, but no AI/ML specific experience.", points: 10 },
      { text: "No in-house engineering; we rely entirely on external vendors.", points: 0 }
    ]
  },
  {
    id: 'c1', pillar: 'Compliance', icon: <Scale className="w-5 h-5 text-amber-500" />,
    question: "Are you operating under strict regulatory frameworks (GDPR, HIPAA, EU AI Act)?",
    options: [
      { text: "Yes, and we have automated compliance tracking tools in place.", points: 25 },
      { text: "Yes, but compliance is handled manually via spreadsheets/legal reviews.", points: 10 },
      { text: "No major regulatory burdens, or we are unaware of our obligations.", points: 0 }
    ]
  },
  {
    id: 'c2', pillar: 'Compliance', icon: <Scale className="w-5 h-5 text-amber-500" />,
    question: "Can you generate a Software/AI Bill of Materials (AI-BOM) for your systems?",
    options: [
      { text: "Yes, we generate dynamic SPDX AI-BOMs for all vendor AI tools.", points: 25 },
      { text: "We maintain a manual inventory spreadsheet of our software.", points: 10 },
      { text: "We do not track our AI vendor usage systematically.", points: 0 }
    ]
  }
];

export default function LlmReadinessGrader() {
  const { user, token, isImpersonating } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  const [answers, setAnswers] = useState({});
  const [results, setResults] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!user || !user.org_id) return;
      try {
        setLoading(true);
        const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
        const res = await axios.get(`${baseURL}/api/sprint/readiness`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.data && res.data.readiness_score > 0) {
          setMessage({ 
            type: 'info', 
            text: `Welcome back. Your previous baseline readiness score was ${res.data.readiness_score}/100. Retake the assessment below to update your enterprise heat map.` 
          });
        }
      } catch (error) {
        console.error("Failed to load readiness data", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user?.org_id, token]);

  const handleSelect = (questionId, points, pillar, text) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: { points, pillar, text }
    }));
  };

  const calculateAndSave = async () => {
    if (Object.keys(answers).length < QUESTION_BANK.length) {
      setMessage({ type: 'error', text: 'Please answer all 8 questions to generate your diagnostic report.' });
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (isImpersonating) {
      setMessage({ type: 'error', text: 'Impersonation Mode: Cannot save new assessments.' });
      return;
    }

    const scores = { Data: 0, Security: 0, Infrastructure: 0, Compliance: 0 };
    let totalPoints = 0;
    
    Object.values(answers).forEach(ans => {
      scores[ans.pillar] += ans.points;
      totalPoints += ans.points;
    });

    const report = {
      overall: Math.round((totalPoints / 200) * 100),
      pillars: {
        Data: Math.round((scores.Data / 50) * 100),
        Security: Math.round((scores.Security / 50) * 100),
        Infrastructure: Math.round((scores.Infrastructure / 50) * 100),
        Compliance: Math.round((scores.Compliance / 50) * 100),
      }
    };

    setResults(report);
    window.scrollTo({ top: 0, behavior: 'smooth' });

    const payload = {
      readiness_score: report.overall,
      data_quality_score: Math.round(report.pillars.Data / 10), 
      cloud_infrastructure: report.pillars.Infrastructure >= 80 ? 'Cloud-Native' : 'Hybrid',
      data_silos: JSON.stringify(report.pillars), 
      security_posture: "Advanced Assessment Completed" 
    };

    setSaving(true);
    try {
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      await axios.post(`${baseURL}/api/sprint/readiness`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage({ type: 'success', text: 'Executive Readiness Assessment successfully recorded to your profile.' });
    } catch (error) {
      console.error("Save failed", error);
      setMessage({ type: 'error', text: 'Report generated, but failed to save to database.' });
    } finally {
      setSaving(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-emerald-600 bg-emerald-50 border-emerald-200';
    if (score >= 50) return 'text-amber-600 bg-amber-50 border-amber-200';
    return 'text-red-600 bg-red-50 border-red-200';
  };

  const getProgressBarColor = (score) => {
    if (score >= 80) return 'bg-emerald-500';
    if (score >= 50) return 'bg-amber-500';
    return 'bg-red-500';
  };

  if (loading) return (
    <div className="flex flex-col items-center justify-center min-h-[50vh] w-full">
      <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1, ease: "linear" }} className="w-10 h-10 border-4 border-slate-200 border-t-blue-600 rounded-full mb-4" />
      <div className="text-slate-500 font-medium">Loading Assessment Profile...</div>
    </div>
  );

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-4xl mx-auto p-8 pb-24">
      
      {/* HEADER */}
      <div className="mb-10 text-center">
        <div className="inline-flex items-center justify-center p-3 bg-blue-50 rounded-2xl mb-4">
          <Activity className="w-8 h-8 text-blue-600" />
        </div>
        <h1 className="text-4xl font-black text-slate-900 tracking-tight">LLM Readiness Grader</h1>
        <p className="text-slate-500 mt-3 text-lg max-w-2xl mx-auto">
          Evaluate your enterprise architecture across Data, Security, Infrastructure, and Compliance to determine your operational readiness for Generative AI.
        </p>
      </div>

      {/* MESSAGES */}
      {message.text && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className={`p-4 rounded-xl mb-8 text-sm font-bold border ${
          message.type === 'success' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 
          message.type === 'info' ? 'bg-blue-50 text-blue-700 border-blue-200' :
          'bg-red-50 text-red-700 border-red-200'
        }`}>
          {message.type === 'success' ? '✓ ' : message.type === 'error' ? '⚠ ' : 'ℹ '}{message.text}
        </motion.div>
      )}

      {isImpersonating && !results && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 p-4 rounded-xl mb-8 flex items-center gap-3 text-sm font-bold">
          <Lock className="w-5 h-5 shrink-0" />
          You are impersonating a client. You may view and click through the assessment, but results will not be saved to their database.
        </div>
      )}

      {!results ? (
        <motion.div variants={containerVariants} initial="hidden" animate="show" className="space-y-8">
          {QUESTION_BANK.map((q, index) => (
            <motion.div variants={itemVariants} key={q.id} className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm transition-all hover:shadow-md">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-slate-50 rounded-lg border border-slate-100">
                  {q.icon}
                </div>
                <div>
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-400">{q.pillar} Pillar</span>
                  <h3 className="text-xl font-bold text-slate-800 leading-snug">{index + 1}. {q.question}</h3>
                </div>
              </div>
              
              <div className="space-y-3 pl-14">
                {q.options.map((opt, i) => {
                  const isSelected = answers[q.id]?.points === opt.points;
                  return (
                    <button
                      key={i}
                      onClick={() => handleSelect(q.id, opt.points, q.pillar, opt.text)}
                      className={`w-full text-left p-4 rounded-xl border-2 transition-all flex items-center justify-between group ${
                        isSelected 
                          ? 'border-blue-600 bg-blue-50/50 text-blue-900 font-bold shadow-sm' 
                          : 'border-slate-100 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      <span>{opt.text}</span>
                      {isSelected && <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }}><CheckCircle2 className="w-5 h-5 text-blue-600 shrink-0" /></motion.div>}
                    </button>
                  );
                })}
              </div>
            </motion.div>
          ))}

          <motion.div variants={itemVariants} className="pt-6 flex justify-end">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={calculateAndSave}
              disabled={saving}
              className="bg-slate-900 hover:bg-blue-600 disabled:bg-slate-400 text-white px-8 py-4 rounded-xl font-bold text-lg flex items-center gap-2 transition-all shadow-md"
            >
              {saving ? 'Analyzing Architecture...' : 'Generate Readiness Report'} <ChevronRight className="w-5 h-5" />
            </motion.button>
          </motion.div>
        </motion.div>
      ) : (
        /* RESULTS DASHBOARD */
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          
          <div className="bg-white rounded-3xl p-10 border border-slate-200 shadow-xl text-center relative overflow-hidden mb-8">
            <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-blue-500 via-emerald-500 to-amber-500"></div>
            <h2 className="text-sm font-bold text-slate-400 uppercase tracking-widest mb-4">Enterprise AI Readiness Index</h2>
            
            <div className="flex justify-center items-end gap-2 mb-4">
              <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", bounce: 0.5 }} className="text-8xl font-black tracking-tighter text-slate-900">
                {results.overall}
              </motion.span>
              <span className="text-3xl font-bold text-slate-400 pb-2">/100</span>
            </div>
            <div className={`inline-block px-6 py-2 rounded-full border text-sm font-bold uppercase tracking-wider ${getScoreColor(results.overall)}`}>
              {results.overall >= 80 ? 'Production Ready' : results.overall >= 50 ? 'Remediation Required' : 'High Risk Profile'}
            </div>
          </div>

          <motion.div variants={containerVariants} initial="hidden" animate="show" className="bg-white rounded-3xl p-10 border border-slate-200 shadow-sm mb-8">
            <h3 className="text-xl font-bold text-slate-900 mb-8 flex items-center gap-2">
              <Activity className="w-6 h-6 text-slate-400" /> Infrastructure Heat Map
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8">
              {Object.entries(results.pillars).map(([pillarName, score]) => (
                <motion.div variants={itemVariants} key={pillarName}>
                  <div className="flex justify-between items-end mb-2">
                    <span className="font-bold text-slate-700">{pillarName} Architecture</span>
                    <span className={`font-black ${getScoreColor(score).split(' ')[0]}`}>{score}%</span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-3 overflow-hidden">
                    <motion.div 
                      initial={{ width: 0 }}
                      animate={{ width: `${score}%` }}
                      transition={{ duration: 1, ease: "easeOut", delay: 0.2 }}
                      className={`h-3 rounded-full ${getProgressBarColor(score)}`}
                    />
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <div className="bg-slate-900 rounded-3xl p-10 text-white text-center flex flex-col items-center shadow-xl">
            <h3 className="text-2xl font-bold mb-4">Next Step: Execution</h3>
            <p className="text-slate-400 mb-8 max-w-xl">
              Based on your readiness index, we recommend generating a structured 6-month deployment roadmap to close your highest-risk gaps before deploying production models.
            </p>
            <motion.button 
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/roadmap')}
              className="bg-blue-600 hover:bg-blue-500 text-white px-8 py-4 rounded-xl font-bold text-lg flex items-center gap-2 transition-all shadow-md"
            >
              Generate 6-Month Roadmap <ArrowRightCircle className="w-6 h-6" />
            </motion.button>
            
            <button 
              onClick={() => {
                setResults(null);
                setAnswers({});
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }} 
              className="mt-6 text-sm text-slate-500 hover:text-slate-300 font-medium underline transition-colors"
            >
              Retake Assessment
            </button>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
}